// `shortlist.last-alert-viewed.service.ng1.ts` (`ShortlistLastAlertViewedService`) — how the
// recruiter last left a hunter list: which alert, the query, the page, the row, and every id the
// search matched. The list reads it back on init (`reloadStateIfNeeded`) and, when it still
// applies, REPLAYS that search instead of starting a new one.
//
// ⚠️ A RESTORE IS NOT A SEARCH, AND THE DIFFERENCE IS ANALYTICS AND WRITES. Measured on the dev
// tenant against upstream: a new search records `HunterResultsLoaded`, sends
// `persistFilterAndSort: true` and calls `updateAlertLastViewed`; a restore does none of the
// three. It asks `getCandidateDetailsByAlertId` / `getClientContactDetailsByAlertId` with
// `persistFilterAndSort: false` and `limitCandidateIds` / `limitClientContactIds` set to the
// stored ids — the SAME result set, re-sorted by the stored query, never a fresh one — and
// leaves the stored state as it was.
//
// ⚠️ IT IS THE PAGE'S OWN `localStorage`, NOT THE EXTENSION'S. `ExtensionStorageService` writes
// `window.localStorage` under `lastAlertViewed`, prefixed `linkedin.` when the shell says the host
// is LinkedIn (`hostPrefixMap`), and mirrors the write through `window.wooSetStorageMessage` only
// where a content script defined one. So it is per ORIGIN and per browser profile: the baseline
// tools open every route in a fresh context seeded with the sign-in's cookies only, and a fresh
// context has nothing here to restore.
import { shortlistSessionHostInfo } from '../shortlistSession.ts'

/** `ExtensionProduct` — the key each product's state is filed under. */
export const ExtensionProduct = { Candidates: 'Candidates', ClientContacts: 'ClientContacts' } as const
export type ExtensionProduct = (typeof ExtensionProduct)[keyof typeof ExtensionProduct]

/** `ExtensionStorageValidityPeriod.StateStorageValidityPeriod` — eight hours. */
export const STATE_STORAGE_VALIDITY_PERIOD = 8 * 60 * 60 * 1000

/** `ExtensionStorageKeys.LastAlertViewed`. */
export const LAST_ALERT_VIEWED_KEY = 'lastAlertViewed'

/** `ShortlistSessionHost.Linkedin` — the one host `hostPrefixMap` prefixes. */
const LINKEDIN_HOST = 2

/**
 * `IGetAlertEntitiesParams` as the list sent it. Upstream stores its own `getDataParams` object
 * verbatim, `atsFilters` and all; the port stores the query it sent, which is the same shape.
 */
export interface StoredDataParams {
  alertId: number
  sortBy?: string
  filter?: Record<string, unknown>
}

/** `IExtensionProductLastAlertViewedState`. */
export interface ExtensionProductLastAlertViewedState {
  timestamp: number
  allEntityIds: number[]
  userId?: number
  selectedAlertId: number
  selectedEntityId?: number
  selectedPage: number
  getDataParams: StoredDataParams
}

/** `ILastAlertViewedState` — version 1, one entry per product. */
export interface LastAlertViewedState {
  version: number
  extensionProductStates: Partial<Record<ExtensionProduct, ExtensionProductLastAlertViewedState>>
}

/** `IOldLastAlertViewedState` — the unversioned, candidates-only shape `getState` migrates. */
interface OldLastAlertViewedState {
  timestamp: number
  allCandidateIds: number[]
  userId?: number
  selectedAlertId: number
  selectedCandidateId?: number
  selectedPage: number
  getDataParams: StoredDataParams
}

/** `calculateLocalStorageKeyForHost` — ATS and None carry no prefix. */
export function lastAlertViewedKey(host: number): string {
  return host === LINKEDIN_HOST ? `linkedin.${LAST_ALERT_VIEWED_KEY}` : LAST_ALERT_VIEWED_KEY
}

/**
 * `getState` — parse, migrating the unversioned shape. A value that is not JSON is treated as
 * absent rather than thrown: upstream would throw out of `$onInit` and never search at all, which
 * is not behaviour worth keeping for a corrupt storage entry.
 */
export function parseLastAlertViewed(raw: string | null | undefined): LastAlertViewedState | null {
  if (raw === null || raw === undefined || raw === '') return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  if ((parsed as { version?: unknown }).version === undefined) {
    const old = parsed as OldLastAlertViewedState
    return {
      version: 1,
      extensionProductStates: {
        [ExtensionProduct.Candidates]: {
          timestamp: old.timestamp,
          allEntityIds: old.allCandidateIds,
          selectedAlertId: old.selectedAlertId,
          ...(old.selectedCandidateId === undefined ? {} : { selectedEntityId: old.selectedCandidateId }),
          selectedPage: old.selectedPage,
          getDataParams: old.getDataParams,
        },
      },
    }
  }
  return parsed as LastAlertViewedState
}

/**
 * `getUnexpiredByExtensionProduct` — the product's entry, unless it is older than eight hours or
 * matched nothing ("better to start a new search").
 */
export function unexpiredProductState(
  raw: string | null | undefined,
  product: ExtensionProduct,
  now: number,
): ExtensionProductLastAlertViewedState | null {
  const state = parseLastAlertViewed(raw)
  const productState = state?.extensionProductStates?.[product]
  if (productState === undefined || productState === null) return null
  if (productState.timestamp < now - STATE_STORAGE_VALIDITY_PERIOD) return null
  if (!Array.isArray(productState.allEntityIds) || productState.allEntityIds.length === 0) return null
  return productState
}

/**
 * The rest of `reloadStateIfNeeded`: a state written by another user, for another alert ("working
 * with multiple computers"), or arriving with an explicit row to move to, is not restored.
 *
 * ⚠️ A STATE WITHOUT A `userId` PASSES, as upstream's `!!state.userId &&` lets it.
 */
export function isRestorable(
  state: ExtensionProductLastAlertViewedState | null,
  context: { userId: number | undefined; alertId: number; moveToEntityId?: number | undefined },
): state is ExtensionProductLastAlertViewedState {
  if (state === null) return false
  if (state.userId !== undefined && state.userId !== null && state.userId !== 0 && state.userId !== context.userId) {
    return false
  }
  if (state.selectedAlertId !== context.alertId) return false
  if (typeof context.moveToEntityId === 'number') return false
  return true
}

/**
 * `getExtensionUserSession().userId` — the view model's `session` (`window.careero.session`,
 * which upstream's session service is created from). `null` when signed out or absent.
 */
export function sessionUserIdOf(session: unknown): number | null {
  if (typeof session !== 'object' || session === null) return null
  const userId = (session as { userId?: unknown }).userId
  return typeof userId === 'number' ? userId : null
}

/** `set` — this product's entry replaced, the other product's kept. */
export function withProductState(
  raw: string | null | undefined,
  product: ExtensionProduct,
  productState: ExtensionProductLastAlertViewedState,
): string {
  const old = parseLastAlertViewed(raw)
  const state: LastAlertViewedState = {
    ...(old ?? { version: 1 }),
    extensionProductStates: { ...(old?.extensionProductStates ?? {}), [product]: productState },
  }
  return JSON.stringify(state)
}

/**
 * `remove` — this product's entry dropped. `null` means the key itself goes: there was nothing,
 * or this product was the only entry.
 */
export function withoutProductState(raw: string | null | undefined, product: ExtensionProduct): string | null {
  const old = parseLastAlertViewed(raw)
  if (old === null) return null
  const states = { ...(old.extensionProductStates ?? {}) }
  if (Object.keys(states).length === 1 && states[product] !== undefined) return null
  delete states[product]
  return JSON.stringify({ ...old, extensionProductStates: states })
}

/**
 * `setPageResults` on a restored page: a page that now starts past the end steps back ONCE —
 * `--this.currPage`, not a clamp to the last page — and never below the first.
 */
export function restoredPage(selectedPage: number, total: number, pageSize: number): number {
  let page = Number.isInteger(selectedPage) && selectedPage > 0 ? selectedPage : 1
  if ((page - 1) * pageSize >= total) page -= 1
  return Math.max(page, 1)
}

/**
 * `resetGetAlertDataParamsToState`: "since we removed sortBy we now have states without sort
 * data, so we set to default" — and the default is per product, and for contacts it is NOT the
 * sort the bar defaults to (`RELEVANCY_HIRING_LIKELIHOOD`, where the bar's default is past
 * activity).
 */
export function restoredSortBy(product: ExtensionProduct, params: StoredDataParams | undefined): string {
  if (params?.sortBy !== undefined && params.sortBy !== null && params.sortBy !== '') return params.sortBy
  return product === ExtensionProduct.Candidates ? 'RELEVANCY' : 'RELEVANCY_HIRING_LIKELIHOOD'
}

/** The stored candidate filter, spelled as the alert's persisted fields the bar is seeded from. */
export function candidateAlertFieldsFromParams(params: StoredDataParams): {
  filterLastRelevancyXDays?: number | null
  filterShowHiddenCandidates: boolean
  filterShowFavoritesCandidatesOnly: boolean
  filterHideViewedCandidates: boolean
  filterShowExportedCandidatesOnly: boolean
  filterShowMessagedCandidatesOnly: boolean
  filterShowActionedAndUnactionedCandidates: boolean
} {
  const filter = params.filter ?? {}
  const days = filter['lastRelevancyXDays']
  return {
    ...(typeof days === 'number' ? { filterLastRelevancyXDays: days } : {}),
    filterShowHiddenCandidates: filter['showHiddenCandidates'] === true,
    filterShowFavoritesCandidatesOnly: filter['showFavoriteCandidatesOnly'] === true,
    filterHideViewedCandidates: filter['hideViewedCandidates'] === true,
    filterShowExportedCandidatesOnly: filter['showExportedCandidatesOnly'] === true,
    filterShowMessagedCandidatesOnly: filter['showMessagedCandidatesOnly'] === true,
    filterShowActionedAndUnactionedCandidates: filter['showActionedAndUnactionedCandidates'] === true,
  }
}

/** The contacts twin — the third spelling of the same six flags. */
export function contactAlertFieldsFromParams(params: StoredDataParams): {
  filterShowHiddenContacts: boolean
  filterShowFavoritesContactsOnly: boolean
  filterHideViewedContacts: boolean
  filterShowExportedContactsOnly: boolean
  filterShowMessagedContactsOnly: boolean
  filterShowActionedAndUnactionedContacts: boolean
} {
  const filter = params.filter ?? {}
  return {
    filterShowHiddenContacts: filter['showHiddenClientContacts'] === true,
    filterShowFavoritesContactsOnly: filter['showFavoriteClientContactsOnly'] === true,
    filterHideViewedContacts: filter['hideViewedClientContacts'] === true,
    filterShowExportedContactsOnly: filter['showExportedClientContactsOnly'] === true,
    filterShowMessagedContactsOnly: filter['showMessagedClientContactsOnly'] === true,
    filterShowActionedAndUnactionedContacts: filter['showActionedAndUnactionedClientContacts'] === true,
  }
}

// ---- storage ----------------------------------------------------------------------------------

interface StorageMirror {
  wooSetStorageMessage?: (key: string, value: string) => void
  wooRemoveStorageMessage?: (key: string) => void
}

function storage(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

/** `ExtensionStorageService.load()` — the key waits for the host the shell reports. */
async function storageKey(): Promise<string> {
  return lastAlertViewedKey((await shortlistSessionHostInfo()).host)
}

function readRaw(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeRaw(key: string, value: string | null): void {
  const mirror = window as unknown as StorageMirror
  try {
    if (value === null) storage()?.removeItem(key)
    else storage()?.setItem(key, value)
  } catch {
    // A full or blocked store loses the restore, which is what a fresh profile has anyway.
  }
  if (value === null) mirror.wooRemoveStorageMessage?.(key)
  else mirror.wooSetStorageMessage?.(key, value)
}

/** `getUnexpired(product)`. */
export async function getUnexpiredLastAlertViewed(
  product: ExtensionProduct,
): Promise<ExtensionProductLastAlertViewedState | null> {
  return unexpiredProductState(readRaw(await storageKey()), product, Date.now())
}

/**
 * `persistState`'s storage half: "no need to save empty state, removing it so it will try to start
 * a new search upon refresh".
 *
 * ⚠️ SERIALISED, because upstream's writes are synchronous and these wait on the host first: an
 * archive persists the shortened list and then the next row's selection, and the second must not
 * land before the first.
 */
let pending: Promise<void> = Promise.resolve()
export function persistLastAlertViewed(
  product: ExtensionProduct,
  productState: Omit<ExtensionProductLastAlertViewedState, 'timestamp'>,
): Promise<void> {
  const timestamp = Date.now()
  pending = pending.then(async () => {
    const key = await storageKey()
    const raw = readRaw(key)
    if (productState.allEntityIds.length === 0) {
      writeRaw(key, withoutProductState(raw, product))
      return
    }
    writeRaw(key, withProductState(raw, product, { ...productState, timestamp }))
  })
  return pending
}

/** `remove(product)` — on entering the alert's editor, which changes what the search matches. */
export function removeLastAlertViewed(product: ExtensionProduct, onlyForAlertId?: number): Promise<void> {
  pending = pending.then(async () => {
    const key = await storageKey()
    const raw = readRaw(key)
    if (onlyForAlertId !== undefined) {
      const current = unexpiredProductState(raw, product, Date.now())
      if (current === null || current.selectedAlertId !== onlyForAlertId) return
    }
    writeRaw(key, withoutProductState(raw, product))
  })
  return pending
}

/**
 * `smoothScroll(row, { offset, containerId })` after a restore: the scroll container is brought to
 * the row's document offset minus the fixed slim headers (`71 + 49 + 73`, plus 39 for the
 * external-sources header when the session is in an ATS — which this port does not model yet).
 */
export const RESTORE_SCROLL_OFFSET = 71 + 49 + 73

export function scrollToRestoredRow(entityId: number, containerId = 'results-slim-scrollable-container'): void {
  const row = document.querySelector(`.shortlist-candidate-slim.cand-${String(entityId)}`)
  const container = document.getElementById(containerId)
  if (!(row instanceof HTMLElement) || container === null) return
  let location = 0
  let element: HTMLElement | null = row
  while (element !== null) {
    location += element.offsetTop
    element = element.offsetParent instanceof HTMLElement ? element.offsetParent : null
  }
  container.scrollTo({ top: Math.max(location - RESTORE_SCROLL_OFFSET, 0), behavior: 'smooth' })
}
